import React, { useState, useEffect } from 'react';
import { Trash2, FolderOpen } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatPrice } from '../../config/pricingConfig';
import { STORAGE_KEY } from '../../lib/estimateAnalytics';

const SavedEstimates = () => {
  const [estimates, setEstimates] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setEstimates(Array.isArray(stored) ? stored : []);
    } catch {
      setEstimates([]);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (!event.key || event.key === STORAGE_KEY) {
        try {
          const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
          setEstimates(Array.isArray(stored) ? stored : []);
        } catch {
          setEstimates([]);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleDelete = (id) => {
    const updated = estimates.filter((e) => e.id !== id);
    setEstimates(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Storage write failed; in-memory state is still updated
    }
  };

  const handleDeleteAll = () => {
    if (window.confirm('Delete all saved estimates?')) {
      setEstimates([]);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storage removal failed; in-memory state is still cleared
      }
    }
  };

  if (estimates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
        <FolderOpen size={48} className="text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-600 mb-2">No saved estimates</h2>
        <p className="text-gray-400 text-sm">
          Use the Cost Estimator to build a resource estimate, then save it here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Saved Estimates</h2>
        <Button
          onClick={handleDeleteAll}
          className="bg-red-500 hover:bg-red-600 text-sm h-9"
        >
          Delete All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {estimates.map((est) => (
          <Card key={est.id} className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-800">{est.name}</h3>
                <p className="text-xs text-gray-400">
                  {new Date(est.savedAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleDelete(est.id)}
                className="text-red-400 hover:text-red-600"
                aria-label="Delete estimate"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <p className="text-2xl font-bold text-teal-600 mb-2">
              {formatPrice(est.total ?? 0)}
            </p>
            <p className="text-xs text-gray-500 mb-3">{est.items?.length ?? 0} resource(s)</p>

            <button
              className="text-teal-600 text-xs hover:underline"
              onClick={() => setExpanded(expanded === est.id ? null : est.id)}
            >
              {expanded === est.id ? 'Hide details ▲' : 'Show details ▼'}
            </button>

            {expanded === est.id && (
              <ul className="mt-2 text-xs text-gray-600 space-y-1 border-t pt-2">
                {(est.items || []).map((item, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{item.type} – {item.sku || '(no SKU)'}</span>
                    <span>
                      {item.unitPrice
                        ? formatPrice(parseFloat(item.unitPrice) * item.quantity * item.hours)
                        : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SavedEstimates;
